// We need to register it again because of Vue instance instantiation issues

import type { EventLocationPreference } from '~/components/common/card/TeameventRequestCard.vue'
import { sendViewWishlist } from '~/composables/useGtmEvents'
import {
  discardMobileDrawerHistory,
  popMobileDrawerHistory,
  pushMobileDrawerHistory,
  registerMobileDrawerCloseHandler,
  registerMobileDrawerIsOpenHandler,
  registerMobileDrawerOpenHandler,
  type MobileDrawerLayer,
} from '~/composables/useDrawerBackNavigation'

export interface UiState {
  isCartSidebarOpen: boolean
  isDateRequestSidebarOpen: boolean
  isWaitlistSidebarOpen: boolean
  isNavigationSidebarOpen: boolean
  isFederatedSearchOpen: boolean
  eventRequestOpenedPage: string | undefined
  dateRequestSidebarRequestLocation: string | undefined
  dateRequestSidebarRequestPreferredLocation:
    | EventLocationPreference
    | undefined
  /** Buy-box party size synced after the user changes quantity on an event page. */
  dateRequestSuggestedTicketCount: number | undefined
}

interface SidebarCloseOptions {
  skipHistory?: boolean
}

const syncDrawerHistoryOnClose = (
  layer: MobileDrawerLayer,
  skipHistory?: boolean
) => {
  if (skipHistory) {
    discardMobileDrawerHistory(layer)
    return
  }

  popMobileDrawerHistory(layer)
}

const setBodyScrollLocked = (locked: boolean) => {
  if (typeof document === 'undefined' || !document.body) return

  document.body.classList.remove('prevent-scroll')
  if (locked) {
    document.body.classList.add('prevent-scroll')
  }
}

export const useUiState = () => {
  if (!import.meta.client) {
    return {
      isCartSidebarOpen: ref(false),
      isDateRequestSidebarOpen: ref(false),
      eventRequestOpenedPage: ref(undefined),
      isWaitlistSidebarOpen: ref(false),
      isNavigationSidebarOpen: ref(false),
      isFederatedSearchOpen: ref(false),
      dateRequestSidebarRequestLocation: '',
      dateRequestSidebarRequestPreferredLocation: ref<
        EventLocationPreference | undefined
      >(undefined),
      dateRequestSuggestedTicketCount: ref<number | undefined>(undefined),
      setDateRequestSuggestedTicketCount: () => {},
      toggleCartSidebar: () => {},
      toggleDateRequestSidebar: () => {},
      toggleWaitlistSidebar: () => {},
      toggleNavigationSidebar: () => {},
      toggleFederatedSearchOpen: () => {},
      closeCartSidebar: () => {},
      closeDateRequestSidebar: () => {},
      openDateRequestSidebar: () => {},
      closeWaitlistSidebar: () => {},
    }
  }

  const state = useState<UiState>('uiState', () => ({
    isCartSidebarOpen: false,
    isDateRequestSidebarOpen: false,
    eventRequestOpenedPage: undefined,
    isWaitlistSidebarOpen: false,
    isNavigationSidebarOpen: false,
    isFederatedSearchOpen: false,
    dateRequestSidebarRequestLocation: '',
    dateRequestSidebarRequestPreferredLocation: undefined,
    dateRequestSuggestedTicketCount: undefined,
  }))

  const drawerHandlersRegistered = useState(
    'drawerBackHandlersRegistered',
    () => false
  )

  // const { sendViewCart, sendDateRequestSidebarView } = useGtm()
  const isCartSidebarOpen = computed(() => state.value.isCartSidebarOpen)

  const openCartSidebar = () => {
    if (state.value.isCartSidebarOpen) return

    state.value.isCartSidebarOpen = true
    setBodyScrollLocked(true)
  }

  const closeCartSidebar = (options: SidebarCloseOptions = {}) => {
    if (!state.value.isCartSidebarOpen) return

    state.value.isCartSidebarOpen = false
    setBodyScrollLocked(false)

    syncDrawerHistoryOnClose('cart', options.skipHistory)
  }

  const toggleCartSidebar = (send_to_gtm: boolean = false) => {
    if (state.value.isCartSidebarOpen) {
      closeCartSidebar()
      return
    }

    state.value.isCartSidebarOpen = true
    setBodyScrollLocked(true)
    pushMobileDrawerHistory('cart')

    if (send_to_gtm) {
      sendViewCart()
    }
  }

  const isDateRequestSidebarOpen = computed(
    () => state.value.isDateRequestSidebarOpen
  )

  const openDateRequestSidebar = () => {
    if (state.value.isDateRequestSidebarOpen) return

    state.value.isDateRequestSidebarOpen = true
    setBodyScrollLocked(true)
  }

  const closeDateRequestSidebar = (options: SidebarCloseOptions = {}) => {
    if (!state.value.isDateRequestSidebarOpen) return

    state.value.isDateRequestSidebarOpen = false
    setBodyScrollLocked(false)

    syncDrawerHistoryOnClose('date-request', options.skipHistory)
  }

  const toggleDateRequestSidebar = (
    openedPage: string | undefined = undefined,
    requestLocation: string | undefined = undefined,
    wantedLocation: EventLocationPreference | undefined = undefined,
    suggestedTicketCount: number | undefined = undefined
  ) => {
    if (state.value.isDateRequestSidebarOpen) {
      closeDateRequestSidebar()
      return
    }

    state.value.isDateRequestSidebarOpen = true
    state.value.eventRequestOpenedPage = openedPage
    state.value.dateRequestSidebarRequestLocation = requestLocation
    state.value.dateRequestSidebarRequestPreferredLocation = wantedLocation

    if (suggestedTicketCount !== undefined) {
      state.value.dateRequestSuggestedTicketCount = suggestedTicketCount
    }

    setBodyScrollLocked(true)
    pushMobileDrawerHistory('date-request')
    sendDateRequestSidebarView(requestLocation || '')
  }

  const eventRequestOpenedPage = computed(
    () => state.value.eventRequestOpenedPage
  )

  const isWaitlistSidebarOpen = computed(
    () => state.value.isWaitlistSidebarOpen
  )

  const openWaitlistSidebar = () => {
    if (state.value.isWaitlistSidebarOpen) return

    state.value.isWaitlistSidebarOpen = true
    setBodyScrollLocked(true)
  }

  const closeWaitlistSidebar = (options: SidebarCloseOptions = {}) => {
    if (!state.value.isWaitlistSidebarOpen) return

    state.value.isWaitlistSidebarOpen = false
    setBodyScrollLocked(false)

    syncDrawerHistoryOnClose('waitlist', options.skipHistory)
  }

  const toggleWaitlistSidebar = () => {
    if (state.value.isWaitlistSidebarOpen) {
      closeWaitlistSidebar()
      return
    }

    state.value.isWaitlistSidebarOpen = true
    setBodyScrollLocked(true)
    pushMobileDrawerHistory('waitlist')
    sendWaitlistSidebarView()
  }
  const isNavigationSidebarOpen = computed(
    () => state.value.isNavigationSidebarOpen
  )
  const toggleNavigationSidebar = () => {
    state.value.isNavigationSidebarOpen = !state.value.isNavigationSidebarOpen
  }

  const isFederatedSearchOpen = computed(
    () => state.value.isFederatedSearchOpen
  )
  const toggleFederatedSearchOpen = () => {
    state.value.isFederatedSearchOpen = !state.value.isFederatedSearchOpen
  }

  const dateRequestSidebarRequestLocation = computed(
    () => state.value.dateRequestSidebarRequestLocation
  )
  const dateRequestSidebarRequestPreferredLocation = computed(
    () => state.value.dateRequestSidebarRequestPreferredLocation
  )
  const dateRequestSuggestedTicketCount = computed(
    () => state.value.dateRequestSuggestedTicketCount
  )
  const setDateRequestSuggestedTicketCount = (
    ticketCount: number | undefined
  ) => {
    state.value.dateRequestSuggestedTicketCount = ticketCount
  }

  if (!drawerHandlersRegistered.value) {
    drawerHandlersRegistered.value = true
    registerMobileDrawerOpenHandler('cart', openCartSidebar)
    registerMobileDrawerCloseHandler('cart', () => {
      closeCartSidebar({ skipHistory: true })
    })
    registerMobileDrawerIsOpenHandler(
      'cart',
      () => state.value.isCartSidebarOpen
    )
    registerMobileDrawerOpenHandler('waitlist', openWaitlistSidebar)
    registerMobileDrawerCloseHandler('waitlist', () => {
      closeWaitlistSidebar({ skipHistory: true })
    })
    registerMobileDrawerIsOpenHandler(
      'waitlist',
      () => state.value.isWaitlistSidebarOpen
    )
  }

  return {
    isCartSidebarOpen,
    isDateRequestSidebarOpen,
    eventRequestOpenedPage,
    isWaitlistSidebarOpen,
    isNavigationSidebarOpen,
    isFederatedSearchOpen,
    dateRequestSidebarRequestLocation,
    dateRequestSidebarRequestPreferredLocation,
    dateRequestSuggestedTicketCount,
    setDateRequestSuggestedTicketCount,
    toggleCartSidebar,
    toggleDateRequestSidebar,
    toggleWaitlistSidebar,
    toggleNavigationSidebar,
    toggleFederatedSearchOpen,
    closeCartSidebar,
    closeDateRequestSidebar,
    openDateRequestSidebar,
    closeWaitlistSidebar,
  }
}

const SIDEBAR_HISTORY_LAYER_BY_KEY: Record<string, MobileDrawerLayer> = {
  activeOffers: 'active-offers',
  wishlist: 'wishlist',
}

export const useSidebarUiState = (
  key: string,
  { gtmCb }: { gtmCb?: (...args: any) => any } = {}
) => {
  if (!import.meta.client) {
    return {
      isOpen: false,
      close: () => {},
      toggle: () => {},
    }
  }

  const isOpen = useState(`singleUiState-${key}`, () => false)
  const historyLayer = SIDEBAR_HISTORY_LAYER_BY_KEY[key]

  const open = () => {
    if (isOpen.value) return

    isOpen.value = true
    setBodyScrollLocked(true)
  }

  const close = (options: SidebarCloseOptions = {}) => {
    if (!isOpen.value) return

    isOpen.value = false
    setBodyScrollLocked(false)

    if (historyLayer) {
      syncDrawerHistoryOnClose(historyLayer, options.skipHistory)
    }
  }

  const toggle = (send_to_gtm: boolean = false) => {
    if (isOpen.value) {
      close()
      return
    }

    isOpen.value = true
    setBodyScrollLocked(true)

    if (historyLayer) {
      pushMobileDrawerHistory(historyLayer)
    }

    if (send_to_gtm && gtmCb) {
      gtmCb()
    }
  }

  if (historyLayer) {
    registerMobileDrawerOpenHandler(historyLayer, open)
    registerMobileDrawerCloseHandler(historyLayer, () => {
      close({ skipHistory: true })
    })
    registerMobileDrawerIsOpenHandler(historyLayer, () => isOpen.value)
  }

  return {
    isOpen,
    toggle,
    close,
  }
}

export const useWishlistSidebarUiState = () => {
  const { wishlist } = useWishlist()
  return useSidebarUiState('wishlist', {
    gtmCb: () => sendViewWishlist(wishlist.value),
  })
}

export const useActiveOffersSidebarUiState = () => {
  return useSidebarUiState('activeOffers')
}
